import React, { useState, useEffect, useRef } from 'react';

import { arrayOf, object } from 'prop-types';
import { get, isEqual } from 'lodash';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import arrayMutators from 'final-form-arrays';
import { propTypes } from '../../util/types';
import HasTruncation from '../Truncate/HasTruncation';
import Modal from '../Modal/Modal';
import SafeHtml from '../SafeHtml/SafeHtml';
import { Form, NamedLink, PrimaryButton, Breadcrumbs, Banner } from '..';
import TransactionListingHeading from '../TransactionListingHeading/TransactionListingHeading';
import {
  InquiryWizardStepActivities,
  InquiryWizardStepPackages,
  InquiryWizardStepSpecies,
  InquiryWizardStepParty,
  InquiryWizardStepSummary,
} from './steps';
import InquiryWizardStepSignup from './steps/InquiryWizardStepSignup';
import InquiryWizardSection from './InquiryWizardSection';
import InquiryWizardPanelHeading from './InquiryWizardPanelHeading';
import css from './InquiryWizard.module.css';
import {
  useInquiryWizard,
  STEP_ACTIVITY,
  STEP_PACKAGES,
  STEP_SPECIES,
  STEP_PARTY,
  STEP_SIGNUP,
  STEP_REVIEW,
} from './InquiryWizardContext';

import InquiryBreakdown from './InquiryBreakdown';
import InquiryItinerary from './InquiryItinerary';
import InquiryPriceIncludes from './InquiryPriceIncludes';
import InquiryPackageFlow from './InquiryPackageFlow';
import InquiryChoiceFlow from './InquiryChoiceFlow';
import InquiryEmailGate from './InquiryEmailGate';
import { isFlexiblePricingEnabled, getFlexiblePricingData } from '../../util/package';
import { rememberInquiryReturnPath } from '../../util/inquiryReturn';
import {
  canSendInquiry,
  hasSavedInquiryDraft,
  isInquiryEmailUnverified,
  isNestedFormSubmit,
} from './inquiryAccess';

const useWindowWidth = () => {
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleResize = () => setWindowWidth(window.innerWidth);

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
    return undefined;
  }, []);

  return windowWidth;
};

const InquiryWizardTitle = ({ step, formValues, listing }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const { selectedPackage } = formValues || {};
  const { description } = selectedPackage || {};

  const stepDescription = description;

  const provider = listing.author;
  const displayName = get(provider, 'attributes.profile.displayName');
  const displayNameParts = displayName.split(' ');

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  switch (step.label) {
    case STEP_SPECIES.label:
      return (
        <InquiryWizardSection>
          <InquiryWizardPanelHeading
            title={
              <FormattedMessage
                id="InquiryWizard.speciesTitle"
                values={{
                  landowner: displayNameParts[0],
                }}
              />
            }
          />
        </InquiryWizardSection>
      );
    case STEP_PARTY.label:
    case STEP_REVIEW.label:
      return (
        <>
          <InquiryWizardSection>
            <InquiryWizardPanelHeading
              title={<FormattedMessage id="InquiryWizard.packageDetails" />}
            />

            <div className={css.priceIncludesMobile}>
              <InquiryPriceIncludes packageInfo={selectedPackage} listing={listing} />
            </div>

            <div className={css.descriptionWrapper}>
              <HasTruncation onShowMore={handleOpenModal}>
                <SafeHtml html={stepDescription} />
              </HasTruncation>
            </div>
          </InquiryWizardSection>
          <Modal
            id="InquiryPage.descriptionModal"
            isOpen={isModalOpen}
            onClose={() => setModalOpen(!isModalOpen)}
            contentClassName={css.modalContent}
            containerClassName={css.containerStyles}
          >
            <div className={css.contentWrapper}>
              <div className={css.contentHeader}>
                <FormattedMessage id="PackagePage.descriptionTitle" />
              </div>
              <SafeHtml html={description} />
            </div>
          </Modal>
        </>
      );
    default:
      return null;
  }
};

const InquiryWizardStatus = ({ stepIndex, steps, params }) => {
  const breadcrumbs = [
    <NamedLink
      className={css.breadcrumbLink}
      name="ListingPage"
      params={{
        ...params,
      }}
    >
      Listing
    </NamedLink>,
  ];

  const breadcrumbsSteps = steps.slice(0, stepIndex + 1);

  breadcrumbsSteps.forEach((item, index) => {
    if (index < breadcrumbsSteps.length - 1) {
      breadcrumbs.push(
        <NamedLink
          className={css.breadcrumbLink}
          name="NewInquiryPage"
          params={{
            ...params,
            step: item.path,
          }}
        >
          {item.label}
        </NamedLink>
      );
    } else {
      breadcrumbs.push(item.label);
    }
  });

  return (
    <InquiryWizardSection>
      <div className={css.statusTitle}>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
      </div>
    </InquiryWizardSection>
  );
};

const InquiryWizardSteps = ({
  step,
  setStep,
  listing,
  timeSlots,
  form,
  bookingDateText,
  onNext,
  values,
  currentUser,
  history,
  errorMessage,
  sendEnquiryInProgress,
  inquirySubmitAttempt,
  onSubmit,
}) => {
  switch (step.label) {
    case STEP_ACTIVITY.label:
      return <InquiryWizardStepActivities listing={listing} form={form} onNext={onNext} />;
    case STEP_PACKAGES.label:
      return (
        <InquiryWizardStepPackages
          listing={listing}
          activity={values.activity}
          form={form}
          onNext={onNext}
          setStep={setStep}
          currentUser={currentUser}
          history={history}
        />
      );
    case STEP_SPECIES.label:
      return <InquiryWizardStepSpecies listing={listing} />;
    case STEP_PARTY.label:
      return (
        <InquiryWizardStepParty
          listing={listing}
          timeSlots={timeSlots}
          formValues={form.getState().values}
          onChange={dates => {
            form.change('bookingDates', dates);
            form.change('dates', dates);
          }}
          text={bookingDateText}
          form={form}
        />
      );

    case STEP_REVIEW.label:
      return <InquiryWizardStepSummary listing={listing} formValues={form.getState().values} />;
    case STEP_SIGNUP.label:
      return (
        <InquiryWizardStepSignup
          formValues={form.getState().values}
          listing={listing}
          currentUser={currentUser}
          errorMessage={errorMessage}
          inquirySubmitInFlight={sendEnquiryInProgress}
          inquirySubmitAttempt={inquirySubmitAttempt}
          onSubmitInquiry={() => onSubmit(values)}
        />
      );
    default:
      return null;
  }
};

InquiryWizardSteps.propTypes = {
  listing: object.isRequired,
};

const InquiryWizardControls = ({
  intl,
  onNext,
  isLastStep,
  disabled,
  isLoading,
  currentUser,
  listing,
  followSubjectMutation,
  onSignupFormTab,
  errorMessage,
  isInquirySubmitLocked,
  formValues,
}) => {
  const listingId = listing?.id?.uuid;
  const userId = currentUser?.id?.uuid;
  const followProperty = () => {
    if (currentUser.id) {
      followSubjectMutation(userId, listingId, { sms: true, email: true });
    }
  };

  const sendLocked = () =>
    typeof isInquirySubmitLocked === 'function' ? isInquirySubmitLocked() : false;
  const blockSend = event => {
    if (!sendLocked()) {
      return false;
    }
    event.preventDefault();
    return true;
  };

  return (
    <div className={css.controlsContainer}>
      {errorMessage && <div className={css.errorMessage}>{errorMessage}</div>}
      {isLastStep && !onSignupFormTab && isInquiryEmailUnverified(currentUser) ? (
        <InquiryEmailGate
          currentUser={currentUser}
          saved={hasSavedInquiryDraft(formValues)}
          landownerName=""
        />
      ) : null}
      <div className={css.controls}>
        {!isLastStep && (
          <>
            <PrimaryButton
              type="button"
              className={css.nextBtnMobile}
              isFullWidth
              disabled={disabled}
              onClick={() => onNext()}
            >
              {intl.formatMessage({ id: 'InquiryWizard.next' })}
            </PrimaryButton>
            <PrimaryButton
              dataTestId="send-inquiry-button"
              type="button"
              isFullWidth
              className={css.nextBtnDesktop}
              disabled={disabled}
              onClick={() => onNext()}
            >
              {intl.formatMessage({ id: 'InquiryWizard.next' })}
            </PrimaryButton>
          </>
        )}

        {isLastStep && !onSignupFormTab && !isInquiryEmailUnverified(currentUser) && (
          <>
            <PrimaryButton
              type="submit"
              isFullWidth
              inProgress={isLoading}
              disabled={disabled || isLoading}
              ready={false}
              className={css.nextBtnMobile}
              onClick={event => {
                if (blockSend(event)) {
                  return;
                }
                followProperty();
              }}
            >
              {intl.formatMessage({ id: 'InquiryWizard.sendInquiry' })}
            </PrimaryButton>
            <PrimaryButton
              dataTestId="send-inquiry-button"
              type="submit"
              isFullWidth
              inProgress={isLoading}
              disabled={disabled || isLoading}
              ready={false}
              className={css.nextBtnDesktop}
              onClick={event => {
                if (blockSend(event)) {
                  return;
                }
                followProperty();
              }}
            >
              {intl.formatMessage({ id: 'InquiryWizard.sendInquiry' })}
            </PrimaryButton>
          </>
        )}
      </div>
      {!onSignupFormTab && (
        <div className={css.controlsFooter}>
          <Banner
            header={intl.formatMessage({ id: 'InquiryWizard.footerNoteHeader' })}
            body={intl.formatMessage({ id: 'InquiryWizard.footerNoteText' })}
          />
        </div>
      )}
    </div>
  );
};

const InquiryWizard = ({
  intl,
  listing,
  timeSlots,
  sendEnquiryInProgress,
  currentUser,
  history,
  location,
  match,
  followSubjectMutation,
  errorMessage,
  isInquirySubmitLocked,
  inquirySubmitAttempt,
  ...props
}) => {
  const { params } = match;
  const windowWidth = useWindowWidth();

  const {
    steps,
    stepIndex,
    step,
    next,
    isFirst,
    isLast,
    setFormValues,
    formValues,
    isPackageInquiry,
  } = useInquiryWizard();

  const hideControls = [STEP_ACTIVITY.label, STEP_PACKAGES.label].includes(steps[stepIndex].label);
  const isChoiceStep = hideControls;

  const wizardStatusComponent = (
    <InquiryWizardStatus stepIndex={stepIndex} steps={steps} isLast={isLast} params={params} />
  );
  const onPartyFormTab = step.label === STEP_PARTY.label;
  const onSignupFormTab = step.path === STEP_SIGNUP.path;
  const onSignupFormTabDesktop = onSignupFormTab && windowWidth >= 768;
  const onReviewTab = step.label === STEP_REVIEW.label;
  const onActivityTab = step.label === STEP_ACTIVITY.label && windowWidth >= 768;
  const onPackagesTab = step.label === STEP_PACKAGES.label && windowWidth >= 768;

  const formValuesRef = useRef(formValues);
  formValuesRef.current = formValues;
  const onSubmitRef = useRef(props.onSubmit);
  onSubmitRef.current = props.onSubmit;
  const autoSubmitUserRef = useRef(null);

  useEffect(() => {
    rememberInquiryReturnPath(location?.pathname);
  }, [location?.pathname]);

  useEffect(() => {
    if (!onSignupFormTab || !canSendInquiry(currentUser)) {
      return undefined;
    }
    const userId = currentUser.id.uuid;
    if (!userId || autoSubmitUserRef.current === userId) {
      return undefined;
    }
    // Wait until the signed-in user is verified. Submitting on the first
    // user id races the session and fails closed for unverified accounts.
    autoSubmitUserRef.current = userId;
    onSubmitRef.current(formValuesRef.current);
    return undefined;
  }, [currentUser, onSignupFormTab]);

  let initialValues = {};

  const packageInfo = formValues?.selectedPackage;

  if (isFlexiblePricingEnabled(packageInfo?.activity)) {
    if (!formValues?.partySize) {
      const flexiblePricingData = getFlexiblePricingData(
        packageInfo?.flexiblePricing,
        packageInfo?.guests?.min
      );
      const packageMinParty = get(packageInfo, 'guests.min', 1);
      const initialPartySize =
        packageInfo?.guests?.max === 0
          ? 2 // adult non hunter + youth hunter
          : flexiblePricingData.adultHunter.quantity;

      const flexibleGuestSize =
        packageInfo?.guests?.max === 0
          ? {
              youthHunter: 1,
              adultNonHunter: 1,
            }
          : {
              adultHunter: packageMinParty || 1,
            };

      initialValues = {
        ...formValues,
        partySize: {
          partySize: initialPartySize,
          flexibleGuestSize,
        },
      };
    } else {
      initialValues = {
        ...formValues,
      };
    }
  } else {
    initialValues = {
      ...formValues,
    };
  }

  return (
    <FinalForm
      {...props}
      keepDirtyOnReinitialize
      initialValues={initialValues}
      mutators={{ ...arrayMutators }}
      render={({ handleSubmit, form, invalid, disabled, values }) => {
        const { startDate, endDate } = values && values.bookingDates ? values.bookingDates : {};

        let submitDisabled = invalid || disabled;

        if (step.label === STEP_PARTY.label) {
          submitDisabled = invalid || disabled || !startDate || !endDate;
        }

        return (
          <Form
            onSubmit={event => {
              const blocked =
                isNestedFormSubmit(event) ||
                (typeof isInquirySubmitLocked === 'function' && isInquirySubmitLocked());
              if (blocked) {
                if (event && event.preventDefault) {
                  event.preventDefault();
                }
                return;
              }
              handleSubmit(event);
            }}
            className={css.formContainer}
          >
            <FormSpy
              subscription={{ values: true }}
              onChange={spyProps => {
                const nextValues = isPackageInquiry
                  ? { ...formValues, ...spyProps.values, entry: 'package' }
                  : spyProps.values;
                if (!isEqual(nextValues, formValues)) {
                  setFormValues(nextValues);
                }
              }}
            />

            {isPackageInquiry ? (
              <InquiryPackageFlow
                intl={intl}
                listing={listing}
                timeSlots={timeSlots}
                form={form}
                values={values}
                invalid={invalid}
                disabled={disabled}
                currentUser={currentUser}
                history={history}
                params={params}
                sendEnquiryInProgress={sendEnquiryInProgress}
                followSubjectMutation={followSubjectMutation}
                errorMessage={errorMessage}
                isDesktop={windowWidth >= 768}
                isInquirySubmitLocked={isInquirySubmitLocked}
                onSubmitInquiry={() => props.onSubmit(values)}
                inquirySubmitAttempt={inquirySubmitAttempt}
              />
            ) : isChoiceStep ? (
              <InquiryChoiceFlow
                params={params}
                breadcrumbs={wizardStatusComponent}
                sidebar={
                  windowWidth >= 768 ? (
                    <TransactionListingHeading
                      listing={listing}
                      currentUser={currentUser}
                      provider={listing.author}
                      customer={currentUser}
                      activity={values.activity}
                      packageId={values?.selectedPackage?.id}
                      isInquiry
                    />
                  ) : null
                }
              >
                <InquiryWizardSteps
                  values={values}
                  step={step}
                  listing={listing}
                  timeSlots={timeSlots}
                  form={form}
                  onNext={next}
                  currentUser={currentUser}
                  history={history}
                  errorMessage={errorMessage}
                  sendEnquiryInProgress={sendEnquiryInProgress}
                  inquirySubmitAttempt={inquirySubmitAttempt}
                  onSubmit={props.onSubmit}
                />
              </InquiryChoiceFlow>
            ) : (
              <div className={css.formInnerContainer}>
                <div className={css.wizardElementsContainer}>
                  <div className={css.desktopWizardStatus}>{wizardStatusComponent}</div>

                  <InquiryWizardTitle
                    listing={listing}
                    step={step}
                    formValues={form.getState().values}
                  />

                  <div className={css.stepsContainer}>
                    <InquiryWizardSteps
                      values={values}
                      step={step}
                      listing={listing}
                      timeSlots={timeSlots}
                      form={form}
                      onNext={next}
                      currentUser={currentUser}
                      history={history}
                      errorMessage={errorMessage}
                      sendEnquiryInProgress={sendEnquiryInProgress}
                      inquirySubmitAttempt={inquirySubmitAttempt}
                      onSubmit={props.onSubmit}
                    />
                  </div>

                  {!hideControls && (
                    <InquiryWizardControls
                      intl={intl}
                      onNext={next}
                      isFirstStep={isFirst}
                      isLastStep={isLast}
                      currentUser={currentUser}
                      listing={listing}
                      disabled={submitDisabled}
                      followSubjectMutation={followSubjectMutation}
                      isLoading={sendEnquiryInProgress}
                      onSignupFormTab={onSignupFormTab}
                      errorMessage={errorMessage}
                      isInquirySubmitLocked={isInquirySubmitLocked}
                      formValues={formValues}
                    />
                  )}
                </div>

                <div className={css.sidebarContainer}>
                  <div className={css.sidebarPanel}>
                    <div>
                      <div className={css.mobileWizardStatus}>{wizardStatusComponent}</div>
                      {(onReviewTab ||
                        onSignupFormTabDesktop ||
                        onPartyFormTab ||
                        onActivityTab ||
                        onPackagesTab) && (
                        <InquiryWizardSection>
                          <TransactionListingHeading
                            listing={listing}
                            currentUser={currentUser}
                            provider={listing.author}
                            customer={currentUser}
                            activity={values.activity}
                            packageId={values?.selectedPackage?.id}
                            isInquiry
                          />
                        </InquiryWizardSection>
                      )}
                    </div>

                    <div className={css.priceIncludesDesktop}>
                      <InquiryPriceIncludes
                        packageInfo={values?.selectedPackage}
                        listing={listing}
                      />
                      <InquiryItinerary listing={listing} formValues={form.getState().values} />
                    </div>

                    {(onPartyFormTab || onReviewTab || onSignupFormTab) && (
                      <div className={css.inquirySectionDesktop}>
                        <InquiryBreakdown listing={listing} formValues={form.getState().values} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Form>
        );
      }}
    />
  );
};

InquiryWizard.defaultProps = {
  timeSlots: null,
  currentUser: null,
};

InquiryWizard.propTypes = {
  intl: intlShape.isRequired,
  listing: object.isRequired,
  timeSlots: arrayOf(propTypes.timeSlot),
  currentUser: propTypes.currentUser,
};

export default withRouter(injectIntl(InquiryWizard));
